import React, { useState, useEffect, useCallback } from "react";
import ContentLoader from "react-content-loader";
import { toast, ToastContainer } from "react-toastify";
import { Building2, Plus, Send, Inbox, CheckCircle, XCircle, Clock, X } from "lucide-react";
import MaterialTable from "material-table";

import { getTrans as t } from "../../Localization/translations";
import subcompanyService from "../../services/company/subcompanyService";
import accessInviteService from "../../services/company/accessInviteService";

// ─── small status badge ───────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending:   { bg: "#fff8e1", color: "#f59e0b", icon: <Clock  size={12} /> },
    accepted:  { bg: "#e8f5e9", color: "#22c55e", icon: <CheckCircle size={12} /> },
    rejected:  { bg: "#fce4ec", color: "#ef4444", icon: <XCircle size={12} /> },
    cancelled: { bg: "#f3f4f6", color: "#6b7280", icon: <X size={12} /> },
  };
  const s = map[status] || map.cancelled;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color,
    }}>
      {s.icon} {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// ─── main component ───────────────────────────────────────────────────────────
function CreateClient() {
  const tableRef = React.createRef();

  // ── tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("clients"); // 'clients' | 'invitations'

  // ── clients tab state ──────────────────────────────────────────────────────
  const [clients, setClients]               = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientError, setClientError]       = useState(null);
  const [plans, setPlans]                   = useState([]);
  const [newClient, setNewClient] = useState({
    company_name: "", email: "", contact_name: "",
    plan: "", subscription_type: "CASH",
    no_of_users: 1, total_amount: 0, start_date: "",
    currency: "USD", country: "", state: "", zip_code: "",
    send_email: false,
  });

  // ── invitations tab state ──────────────────────────────────────────────────
  const [sentInvites,     setSentInvites]     = useState([]);
  const [receivedInvites, setReceivedInvites] = useState([]);
  const [invitesLoading,  setInvitesLoading]  = useState(false);
  const [showInviteForm,  setShowInviteForm]  = useState(false);
  const [inviteError,     setInviteError]     = useState(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [respondingId,    setRespondingId]    = useState(null); // token being responded to
  const [newInvite, setNewInvite] = useState({
    to_email: "",
    invite_type: "request", relationship_type: "managed", message: "",
  });

  // ── fetch on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchClients();
    subcompanyService.getPlans()
      .then((r) => {
        const list = r.data?.data || [];
        setPlans(list);
        if (list.length > 0) setNewClient((prev) => ({ ...prev, plan: list[0].plan_name }));
      })
      .catch(() => {/* non-critical — dropdown stays empty */});
  }, []);

  useEffect(() => {
    if (activeTab === "invitations") fetchInvites();
  }, [activeTab]);

  // ── clients ────────────────────────────────────────────────────────────────
  const fetchClients = () => {
    setClientsLoading(true);
    subcompanyService.getMyClients()
      .then((r) => { setClients(r.data.clients || []); })
      .catch(() => toast.error(t("Failed to fetch companies"), { position: "top-right", autoClose: 2000 }))
      .finally(() => setClientsLoading(false));
  };

  const resetClientForm = () => setNewClient({
    company_name: "", email: "", contact_name: "",
    plan: plans.length > 0 ? plans[0].plan_name : "",
    subscription_type: "CASH",
    no_of_users: 1, total_amount: 0, start_date: "",
    currency: "USD", country: "", state: "", zip_code: "",
    send_email: false,
  });

  const createClientCompany = async () => {
    if (!newClient.company_name.trim() || !newClient.email.trim() || !newClient.contact_name.trim()) {
      setClientError("Company name, email and contact name are required");
      return;
    }
    setClientsLoading(true);
    try {
      await subcompanyService.createClient(newClient);
      toast.success("Client company created successfully", { position: "top-right", autoClose: 2000 });
      resetClientForm();
      setShowClientForm(false);
      fetchClients();
    } catch {
      toast.error("Failed to create client company", { position: "top-right", autoClose: 2000 });
    } finally {
      setClientsLoading(false);
    }
  };

  // ── invitations ────────────────────────────────────────────────────────────
  const fetchInvites = useCallback(() => {
    setInvitesLoading(true);
    Promise.all([
      accessInviteService.listSent(),
      accessInviteService.listReceived(),
    ])
      .then(([sentRes, receivedRes]) => {
        setSentInvites(sentRes.data.results || []);
        setReceivedInvites(receivedRes.data.results || []);
      })
      .catch(() => toast.error("Failed to load invitations", { position: "top-right", autoClose: 2000 }))
      .finally(() => setInvitesLoading(false));
  }, []);

  const resetInviteForm = () => setNewInvite({
    to_email: "",
    invite_type: "request", relationship_type: "managed", message: "",
  });

  const sendInvite = async () => {
    if (!newInvite.to_email.trim()) {
      setInviteError("email is required");
      return;
    }
    setInviteSubmitting(true);
    setInviteError(null);
    try {
      const payload = {
        to_email:          newInvite.to_email.trim(),
        invite_type:       newInvite.invite_type,
        relationship_type: newInvite.relationship_type,
        message:           newInvite.message,
      };

      await accessInviteService.sendInvite(payload);
      toast.success("Invitation sent successfully", { position: "top-right", autoClose: 2000 });
      resetInviteForm();
      setShowInviteForm(false);
      fetchInvites();
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to send invitation";
      setInviteError(msg);
    } finally {
      setInviteSubmitting(false);
    }
  };

  const respondToInvite = async (token, action) => {
    setRespondingId(token);
    try {
      await accessInviteService.respondInvite(token, action);
      toast.success(
        action === "accept" ? "Invitation accepted! Company linked." : "Invitation rejected.",
        { position: "top-right", autoClose: 2000 }
      );
      fetchInvites();
    } catch (err) {
      const msg = err?.response?.data?.error || `Failed to ${action} invitation`;
      toast.error(msg, { position: "top-right", autoClose: 2000 });
    } finally {
      setRespondingId(null);
    }
  };

  const cancelInvite = async (id) => {
    try {
      await accessInviteService.cancelInvite(id);
      toast.success("Invitation cancelled", { position: "top-right", autoClose: 2000 });
      fetchInvites();
    } catch {
      toast.error("Failed to cancel invitation", { position: "top-right", autoClose: 2000 });
    }
  };

  // ── helpers ────────────────────────────────────────────────────────────────
  const pendingReceived = receivedInvites.filter((i) => i.status === "pending").length;

  const tabStyle = (tab) => ({
    padding: "8px 20px",
    border: "none",
    borderBottom: activeTab === tab ? "2px solid #245C7F" : "2px solid transparent",
    background: "none",
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? "#245C7F" : "#6b7280",
    cursor: "pointer",
    fontSize: 14,
    position: "relative",
  });

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid mt-3">
      <ToastContainer />
      <div className="card mt-3">

        {/* ── Card Header ── */}
        <div className="card-header d-flex justify-content-between align-items-center p-3">
          <div>
            <div className="text-lg flex items-center gap-2 text-dark">
              <Building2 className="w-5 h-5 mr-2" />
              {t("Client & Access Management")}
            </div>
            <p className="text-sm text-gray-600">{t("Manage your client companies and access sharing")}</p>
          </div>

          {activeTab === "clients" && (
            <button
              className="btn btn-primary"
              onClick={() => { setShowClientForm(!showClientForm); setClientError(null); resetClientForm(); }}
              disabled={clientsLoading}
            >
              <Plus size={16} className="mr-1" /> {t("Add Client")}
            </button>
          )}

          {activeTab === "invitations" && (
            <button
              className="btn btn-primary"
              onClick={() => { setShowInviteForm(!showInviteForm); setInviteError(null); resetInviteForm(); }}
            >
              <Send size={16} className="mr-1" /> {t("Send Invite")}
            </button>
          )}
        </div>

        {/* ── Tabs ── */}
        <div style={{ borderBottom: "1px solid #e5e7eb", paddingLeft: 16, display: "flex", gap: 4 }}>
          <button style={tabStyle("clients")} onClick={() => setActiveTab("clients")}>
            {t("My Clients")}
          </button>
          <button style={tabStyle("invitations")} onClick={() => setActiveTab("invitations")}>
            {t("Access Invitations")}
            {pendingReceived > 0 && (
              <span style={{
                position: "absolute", top: 4, right: 2,
                background: "#ef4444", color: "#fff",
                borderRadius: "50%", width: 16, height: 16,
                fontSize: 9, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {pendingReceived}
              </span>
            )}
          </button>
        </div>

        {/* ─────────────────────────────────────────────────────────────────── */}
        {/* TAB: CLIENTS                                                        */}
        {/* ─────────────────────────────────────────────────────────────────── */}
        {activeTab === "clients" && (
          <div className="card-body p-3">
            {clientsLoading && !clients.length ? (
              <ContentLoader viewBox="0 0 400 100" backgroundColor="rgba(0,0,0,0.06)" foregroundColor="rgba(0,0,0,0.12)">
                <rect x="0" y="10" rx="4" ry="4" width="400" height="20" />
                <rect x="0" y="40" rx="4" ry="4" width="400" height="20" />
                <rect x="0" y="70" rx="4" ry="4" width="400" height="20" />
              </ContentLoader>
            ) : (
              <>
                <MaterialTable
                  tableRef={tableRef}
                  style={{ backgroundColor: "var(--bs-table-bg)", borderBottomWidth: "1px", boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)" }}
                  title=""
                  columns={[
                    { title: "Company Name",  field: "company_name",  headerStyle: { fontWeight: "bold" } },
                    { title: "Contact Name",  field: "contact_name",  headerStyle: { fontWeight: "bold" } },
                    { title: "Currency",      field: "currency",      headerStyle: { fontWeight: "bold" } },
                    { title: "Plan",          field: "plan",          headerStyle: { fontWeight: "bold" } },
                  ]}
                  data={clients}
                  options={{
                    headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 11 },
                    rowStyle: (_, i) => ({ backgroundColor: i % 2 ? "#fff" : undefined }),
                    paging: false, pageSize: 10, actionsColumnIndex: -1, toolbar: false,
                  }}
                />

                {/* ── Add Client Form ── */}
                {showClientForm && (
                  <div className="mt-4 p-3" style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}>
                    <h5 className="mb-3">{t("Add New Client")}</h5>
                    <div className="row">
                      {[
                        { label: "Company Name",  key: "company_name",  type: "text" },
                        { label: "Email",         key: "email",         type: "email" },
                        { label: "Contact Name",  key: "contact_name",  type: "text" },
                        { label: "Currency",      key: "currency",      type: "text" },
                        { label: "Country",       key: "country",       type: "text" },
                        { label: "State",         key: "state",         type: "text" },
                        { label: "Zip Code",      key: "zip_code",      type: "text" },
                        { label: "No Of Users",   key: "no_of_users",   type: "number" },
                        { label: "Total Amount",  key: "total_amount",  type: "number" },
                        { label: "Start Date",    key: "start_date",    type: "date" },
                      ].map(({ label, key, type }) => (
                        <div className="col-md-4 mb-3" key={key}>
                          <label className="col-form-label">{label}</label>
                          <input
                            type={type} className="form-control"
                            value={newClient[key]}
                            onChange={(e) => setNewClient({ ...newClient, [key]: e.target.value })}
                          />
                        </div>
                      ))}

                      <div className="col-md-4 mb-3">
                        <label className="col-form-label">Plan</label>
                        <select
                          className="form-control"
                          value={newClient.plan}
                          onChange={(e) => setNewClient({ ...newClient, plan: e.target.value })}
                        >
                          {plans.length === 0 ? (
                            <option value="">Loading plans…</option>
                          ) : (
                            plans.map((p) => (
                              <option key={p.plan_name} value={p.plan_name}>
                                {p.plan_name}
                              </option>
                            ))
                          )}
                        </select>
                      </div>

                      <div className="col-md-4 mb-3">
                        <label className="col-form-label">Subscription Type</label>
                        <select className="form-control" value={newClient.subscription_type}
                          onChange={(e) => setNewClient({ ...newClient, subscription_type: e.target.value })}>
                          <option value="CASH">CASH</option>
                          <option value="CARD">CARD</option>
                        </select>
                      </div>

                      <div className="col-md-4 mb-3 d-flex align-items-center pt-4">
                        <input type="checkbox" checked={newClient.send_email}
                          onChange={(e) => setNewClient({ ...newClient, send_email: e.target.checked })} />
                        <label className="ml-2 mb-0">Send Email</label>
                      </div>
                    </div>

                    {clientError && <div className="text-danger small mt-1">{clientError}</div>}

                    <div className="mt-3 d-flex gap-2">
                      <button className="btn btn-secondary" onClick={() => { setShowClientForm(false); setClientError(null); }}>
                        {t("Cancel")}
                      </button>
                      <button className="btn btn-primary ml-2" onClick={createClientCompany} disabled={clientsLoading}>
                        {clientsLoading ? t("Saving…") : t("Save")}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─────────────────────────────────────────────────────────────────── */}
        {/* TAB: ACCESS INVITATIONS                                             */}
        {/* ─────────────────────────────────────────────────────────────────── */}
        {activeTab === "invitations" && (
          <div className="card-body p-3">

            {/* ── Send Invite Form ── */}
            {showInviteForm && (
              <div className="mb-4 p-3" style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}>
                <h5 className="mb-1">{t("Send Access Invitation")}</h5>
                <p className="text-muted small mb-3">
                  Invite another company to share access. They must accept before any link is created.
                </p>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="col-form-label">Email <span className="text-danger">*</span></label>
                    <input
                      type="email" className="form-control"
                      placeholder="owner@theircompany.com"
                      value={newInvite.to_email}
                      onChange={(e) => setNewInvite({ ...newInvite, to_email: e.target.value })}
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="col-form-label">Invite Type</label>
                    <select className="form-control" value={newInvite.invite_type}
                      onChange={(e) => setNewInvite({ ...newInvite, invite_type: e.target.value })}>
                      <option value="request">Request — I want to manage them</option>
                      <option value="give">Give — I want them to manage me</option>
                    </select>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="col-form-label">Relationship Type</label>
                    <select className="form-control" value={newInvite.relationship_type}
                      onChange={(e) => setNewInvite({ ...newInvite, relationship_type: e.target.value })}>
                      <option value="managed">Managed</option>
                      {/* <option value="white_label">White Label</option>
                      <option value="partner">Partner</option> */}
                    </select>
                  </div>

                  <div className="col-md-8 mb-3">
                    <label className="col-form-label">Message (optional)</label>
                    <input
                      type="text" className="form-control"
                      placeholder="Add a short note for the other company…"
                      value={newInvite.message}
                      onChange={(e) => setNewInvite({ ...newInvite, message: e.target.value })}
                    />
                  </div>
                </div>

                {/* invite type hint */}
                <div className="mb-3 p-2" style={{ background: "#f0f9ff", borderRadius: 6, fontSize: 12, color: "#0369a1" }}>
                  {newInvite.invite_type === "request"
                    ? "✦ On accept: your company will be able to manage their company."
                    : "✦ On accept: they will be able to manage your company."}
                </div>

                {inviteError && <div className="text-danger small mb-2">{inviteError}</div>}

                <div className="d-flex gap-2">
                  <button className="btn btn-secondary" onClick={() => { setShowInviteForm(false); setInviteError(null); }}>
                    {t("Cancel")}
                  </button>
                  <button className="btn btn-primary ml-2" onClick={sendInvite} disabled={inviteSubmitting}>
                    {inviteSubmitting ? "Sending…" : <><Send size={14} className="mr-1" /> Send Invitation</>}
                  </button>
                </div>
              </div>
            )}

            {invitesLoading ? (
              <ContentLoader viewBox="0 0 400 80" backgroundColor="rgba(0,0,0,0.06)" foregroundColor="rgba(0,0,0,0.12)">
                <rect x="0" y="0"  rx="4" ry="4" width="400" height="18" />
                <rect x="0" y="28" rx="4" ry="4" width="400" height="18" />
                <rect x="0" y="56" rx="4" ry="4" width="400" height="18" />
              </ContentLoader>
            ) : (
              <>
                {/* ── Received Invitations ── */}
                <div className="mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Inbox size={16} color="#245C7F" />
                    <strong style={{ fontSize: 14, color: "#245C7F" }}>Received Invitations</strong>
                    {pendingReceived > 0 && (
                      <span className="badge badge-danger ml-1">{pendingReceived} pending</span>
                    )}
                  </div>

                  {receivedInvites.length === 0 ? (
                    <p className="text-muted small">No invitations received yet.</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="table table-sm" style={{ fontSize: 13 }}>
                        <thead style={{ background: "#f8fafc" }}>
                          <tr>
                            <th>From Company</th>
                            <th>Type</th>
                            <th>Relationship</th>
                            <th>Message</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receivedInvites.map((inv) => (
                            <tr key={inv.id}>
                              <td><strong>{inv.from_company_name}</strong></td>
                              <td>
                                <span style={{ fontSize: 11 }}>
                                  {inv.invite_type === "request"
                                    ? "Wants to manage you"
                                    : "Wants you to manage them"}
                                </span>
                              </td>
                              <td style={{ textTransform: "capitalize" }}>{inv.relationship_type}</td>
                              <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {inv.message || <span className="text-muted">—</span>}
                              </td>
                              <td><StatusBadge status={inv.status} /></td>
                              <td>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}</td>
                              <td>
                                {inv.status === "pending" ? (
                                  <div className="d-flex gap-1">
                                    <button
                                      className="btn btn-sm btn-success"
                                      disabled={respondingId === inv.token}
                                      onClick={() => respondToInvite(inv.token, "accept")}
                                      style={{ fontSize: 11, padding: "2px 10px" }}
                                    >
                                      {respondingId === inv.token ? "…" : "Accept"}
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger ml-1"
                                      disabled={respondingId === inv.token}
                                      onClick={() => respondToInvite(inv.token, "reject")}
                                      style={{ fontSize: 11, padding: "2px 10px" }}
                                    >
                                      Reject
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: 11 }}>
                                    {inv.responded_at ? new Date(inv.responded_at).toLocaleDateString() : "—"}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* ── Sent Invitations ── */}
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Send size={16} color="#245C7F" />
                    <strong style={{ fontSize: 14, color: "#245C7F" }}>Sent Invitations</strong>
                  </div>

                  {sentInvites.length === 0 ? (
                    <p className="text-muted small">No invitations sent yet.</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="table table-sm" style={{ fontSize: 13 }}>
                        <thead style={{ background: "#f8fafc" }}>
                          <tr>
                            <th>To Company</th>
                            <th>Type</th>
                            <th>Relationship</th>
                            <th>Message</th>
                            <th>Status</th>
                            <th>Sent</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sentInvites.map((inv) => (
                            <tr key={inv.id}>
                              <td><strong>{inv.to_company_name}</strong></td>
                              <td>
                                <span style={{ fontSize: 11 }}>
                                  {inv.invite_type === "request"
                                    ? "Manage them"
                                    : "Let them manage me"}
                                </span>
                              </td>
                              <td style={{ textTransform: "capitalize" }}>{inv.relationship_type}</td>
                              <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {inv.message || <span className="text-muted">—</span>}
                              </td>
                              <td><StatusBadge status={inv.status} /></td>
                              <td>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}</td>
                              <td>
                                {inv.status === "pending" && (
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => cancelInvite(inv.id)}
                                    style={{ fontSize: 11, padding: "2px 10px" }}
                                  >
                                    Cancel
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateClient;
